<template>
  <v-container fill-height fluid class="pa-0 login-container">
    <v-row no-gutters class="fill-height login-row">
      <v-col class="pa-0 image-column">
        <v-img 
          :src="loginImgSrc" 
          class="fill-height"
          position="left center"
          cover
          style="max-width: 1600px; max-height: 1300px;"
          eager
          @error="handleImageError"
          @load="handleImageLoad"
        >
          <template v-slot:placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
            </v-row>
          </template>
        </v-img>
      </v-col>
      
      <v-col 
        class="pa-0 d-flex align-center justify-center white form-column"
      >
        <div class="login-form-wrapper px-6 py-4">

          <div class="logo-container">
            <app-headerlogo />
          </div>

          <!-- OTP verification step -->
          <v-form v-if="otpMode" id="otp-form" v-on:submit.prevent="verifyOtp" class="login-form">
            <div class="otp-header">
              <p class="otp-title">Verification Required</p>
              <p class="otp-subtitle">A 6-digit code was sent to your registered email. Enter it below.</p>
            </div>

            <div class="login-field-group">
              <label class="login-label">Verification Code</label>
              <div class="pill-input-wrap">
                <input
                  id="otpCode"
                  v-model="otpCode"
                  type="text"
                  inputmode="numeric"
                  maxlength="6"
                  class="pill-input otp-input"
                  autocomplete="one-time-code"
                  placeholder="000000"
                />
              </div>
            </div>

            <div v-if="errorMsg" class="login-error">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {{ errorMsg }}
            </div>

            <div v-if="successMsg" class="login-success">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              {{ successMsg }}
            </div>

            <button type="submit" class="login-btn-signin" :disabled="otpLoading">
              {{ otpLoading ? 'Verifying...' : 'Verify' }}
            </button>

            <button type="button" class="login-btn-forgot" @click="resendOtp" :disabled="resendCooldown > 0">
              {{ resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend Code' }}
            </button>

            <button type="button" class="login-btn-back" @click="cancelOtp">Back to Login</button>
          </v-form>

          <!-- Standard login step -->
          <v-form v-else id="login-form" v-on:submit.prevent="login" class="login-form">

            <div class="login-field-group">
              <label class="login-label">{{ $t('login.username') }}</label>
              <div class="pill-input-wrap">
                <input
                  id="username"
                  v-model="username"
                  type="text"
                  class="pill-input"
                  autocomplete="username"
                />
              </div>
            </div>

            <div class="login-field-group">
              <label class="login-label">{{ $t('login.password') }}</label>
              <div class="pill-input-wrap">
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="pill-input"
                  autocomplete="current-password"
                />
                <button type="button" class="pill-eye-btn" @click="showPassword = !showPassword" tabindex="-1">
                  <svg v-if="showPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
            </div>

            <div v-if="errorMsg" class="login-error">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {{ errorMsg }}
            </div>

            <button type="submit" class="login-btn-signin">
              {{ $t("login.signin") }}
            </button>

            <button type="button" class="login-btn-forgot" @click="forgotbtn()">
              {{ $t("login.forgot_password") }}
            </button>

            <div class="login-lang">
              <app-lang-switch />
            </div>

          </v-form>
        </div>
        <app-footer />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
//import i18n from '../../i18n';
import LangSwitch from "../common/LangSwitch";
import axios from "axios";
import crypto from "crypto-js";
import HeaderLogo from '../common/HeaderLogo.vue';
import Footer from "../common/Footer.vue";

export default {
  components: {
    "app-lang-switch": LangSwitch,
    "app-headerlogo": HeaderLogo,
    "app-footer": Footer,
  },

  data() {
    return {
      showPassword: false,
      username: null,
      password: null,
      errorMsg: null,
      ip: "",
      loginImgSrc: null,
      db: this.$route.params.db,
      apiReady: false,
      otpMode: false,
      otpCode: null,
      pendingUserId: null,
      resendCooldown: 0,
      resendTimer: null,
      otpLoading: false,
      successMsg: null,
    };
  },

  methods: {
    //function for redirect links. next example: /en/alertlisting/list
    //starts with / so it cannot have http at the start to have false redirect
    getSafeNext() {
      const next = this.$route.query.next;
      // only allow internal redirects
      if (typeof next === "string" && next.startsWith("/"))
       return next;
      return null;
    },
    forgotbtn() {
      this.$router.push({ name: "forgotpassword", params: {db: this.$route.params.db} }); 
    },

    login() {
      const vm = this;
      vm.errorMsg = null;
      this.$store
        .dispatch("retrvAccessToken", {
          username: vm.username,
          password: String(crypto.AES.encrypt(vm.password, 'secret_t2hr')),
          ip: vm.ip,
          origin: 'browser',
          db: this.$route.params.db
        })
        .then((res) => {
          if (res.data.status == 202 && res.data.otp_required) {
            vm.pendingUserId = res.data.userid;
            vm.otpMode = true;
            vm.otpCode = null;
            vm.startResendCooldown();
          } else if (res.data.status == 400 && res.data.errornum == 1) {
            this.$router.push({
              name: "firsttimelogin",
              params: { id: res.data.userid, db: this.$route.params.db },
              ...(this.getSafeNext() ? { query: { next: this.getSafeNext() } } : {}),
            });
          } else if (res.data.status == 400 && res.data.errornum == 2) {
            this.$router.push({
              name: "firsttimelogin",
              params: { id: res.data.userid, db: this.$route.params.db },
              ...(this.getSafeNext() ? { query: { next: this.getSafeNext() } } : {}),
            });
          } else if (res.data.status == 201) {
              const next = this.getSafeNext();
              if (next) {
                this.$router.push(next);
                return;
              }
            if (res.data.funcaccess.length > 0) {
              this.$router.push({ name: "dashboard" });
            } else {
              this.$router.push({ name: "selfservicehome" });
            }
          } else {
            vm.errorMsg = res.data.error;
          }

          if(this.username == null || this.password == null ||
             this.username == '' || this.password == '' ||
             this.username == null && this.password == null
             || this.username == '' && this.password == ''){
             this.$dialog.alert(this.$t('userandpassrequired')).then((dialog) => {
                console.log('dailog', dialog);
             });
          }
        })
        .catch((error) => {
          vm.errorMsg = error;
        });
    },

    verifyOtp() {
      const vm = this;
      if (vm.otpLoading) return;
      vm.errorMsg = null;
      vm.successMsg = null;
      vm.otpLoading = true;
      this.$store.dispatch('verifyOtp', {
        userid: vm.pendingUserId,
        otp: vm.otpCode,
        ip: vm.ip,
        db: this.$route.params.db,
      }).then((res) => {
        vm.otpLoading = false;
        if (res.data.status == 201) {
          vm.clearResendTimer();
          vm.successMsg = 'Identity verified! Redirecting...';
          vm.errorMsg = null;
          const data = res.data;
          setTimeout(() => {
            const next = vm.getSafeNext();
            if (next) { vm.$router.push(next); return; }
            if (data.funcaccess.length > 0) {
              vm.$router.push({ name: 'dashboard' });
            } else {
              vm.$router.push({ name: 'selfservicehome' });
            }
          }, 1200);
        } else {
          vm.errorMsg = res.data.error;
        }
      }).catch((error) => {
        vm.otpLoading = false;
        vm.errorMsg = error;
      });
    },

    resendOtp() {
      const vm = this;
      vm.errorMsg = null;
      this.$store.dispatch('resendOtp', {
        userid: vm.pendingUserId,
        db: this.$route.params.db,
      }).then((res) => {
        if (res.data.status == 201) {
          vm.successMsg = 'A new verification code has been sent to your email.';
          vm.errorMsg = null;
          vm.startResendCooldown();
        } else {
          vm.errorMsg = res.data.error;
          vm.successMsg = null;
        }
      }).catch((error) => {
        vm.errorMsg = error;
        vm.successMsg = null;
      });
    },

    cancelOtp() {
      this.otpMode = false;
      this.otpCode = null;
      this.pendingUserId = null;
      this.errorMsg = null;
      this.clearResendTimer();
    },

    startResendCooldown() {
      this.resendCooldown = 60;
      this.clearResendTimer();
      this.resendTimer = setInterval(() => {
        if (this.resendCooldown > 0) {
          this.resendCooldown--;
        } else {
          this.clearResendTimer();
        }
      }, 1000);
    },

    clearResendTimer() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
      this.resendCooldown = 0;
    },

    async loadLoginImg() {
      const vm = this;

      if (!this.apiReady) {
        console.log("API not ready yet, skipping image load");
        return;
      }

      try {
        const res = await this.$store.dispatch('get', {url: "/loginimg/all/" + this.db});

        // console.log("Response received:", res.data);

        if (res.data.rec.length > 0 && res.data.rec[0].loginImg) {
          const imgData = res.data.rec[0].loginImg;
          console.log("Image data length:", imgData.length);
          // console.log("Image data starts with:", imgData.substring(0, 50));

          vm.loginImgSrc = imgData;
          return;
        } else {
          console.log("No image found in response");
        }
      } catch(err) {
        console.error("Error loading table img:", err);
      }
    },

    handleImageError(error) {
      console.log("Image failed to load:", error);
    },

    handleImageLoad() {
      console.log("Image loaded successfully");
    }
  },

  async mounted() {
    this.$root.$on('api-ready', async() => {
      // console.log("API is ready, loading images...");
      this.apiReady = true;

      if (this.db && this.db !== 'null') {
        await this.loadLoginImg();
      }
    });

    if (this.$store.getters.apiUrl) {
      // console.log("API already configured, loading images...");
      this.apiReady = true;

      if (this.db && this.db !== 'null') {
        await this.loadLoginImg();
      }
    }

    axios
      .get("https://api.ipify.org?format=json", {})
      .then((res) => {
        console.log(res);
        this.ip = res.data.ip;
      })
      .catch((error) => {
        console.log(error);
      });
  },

  beforeDestroy() {
    this.$root.$off('api-ready');
    this.clearResendTimer();
  }
};
</script>

<style scoped>
body, html {
  margin: 0; padding: 0; height: 100%; overflow: hidden;
}

/* ── Layout ──────────────────────────────────────── */
.login-container { height: 100vh; width: 100vw; overflow: hidden; position: relative; }
.login-row { flex-wrap: nowrap !important; height: 100%; width: 100%; display: flex !important; }

.image-column {
  position: absolute !important; top: 0; left: 0;
  width: calc(100% - 420px) !important; height: 100vh; z-index: 1;
}
.image-column .v-image { width: 100%; height: 100%; min-height: 100vh; }
.image-column .v-image >>> .v-image__image,
.image-column .v-image::v-deep .v-image__image {
  background-size: cover !important;
  background-position: center center !important;
}

.form-column {
  height: 100vh; width: 420px !important; min-width: 420px !important;
  max-width: 420px !important; flex: 0 0 420px !important;
  min-height: 100vh; position: relative; z-index: 100;
  background: #ffffff !important; margin-left: auto;
  border-left: 1px solid #E8EDF5;
}

@media (max-width: 500px) {
  .image-column { display: none; }
  .form-column { width: 100% !important; min-width: 100% !important; max-width: 100% !important; flex: 1 1 auto !important; margin-left: 0; border-left: none; }
}

/* ── Form wrapper ─────────────────────────────────── */
.login-form-wrapper {
  width: 100%; max-width: 340px;
  position: relative; z-index: 100;
  padding: 0 !important;
}

.logo-container {
  max-width: 340px; width: 100%;
  margin: 0 auto 36px;
  display: flex; justify-content: center;
}

/* ── Form ─────────────────────────────────────────── */
.login-form { width: 100%; }

.login-field-group {
  margin-bottom: 20px;
}

.login-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748B;
  margin-bottom: 6px;
}

/* ── Pill-shaped input wrapper ────────────────────── */
.pill-input-wrap {
  display: flex;
  align-items: center;
  background: #F8FAFF;
  border: 1.5px solid #D1D9F0;
  border-radius: 50px;
  padding: 0 18px;
  height: 50px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.pill-input-wrap:focus-within {
  border-color: #4F6BFF;
  box-shadow: 0 0 0 3px rgba(79,107,255,0.10);
}
.pill-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.95rem;
  color: #1E293B;
  font-family: inherit;
}
.pill-input::placeholder { color: #94A3B8; }
.pill-eye-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #94A3B8;
  display: flex;
  align-items: center;
  border-radius: 50%;
  transition: color 0.15s;
}
.pill-eye-btn:hover { color: #4F6BFF; }

/* ── Error ────────────────────────────────────────── */
.login-error {
  display: flex; align-items: center; gap: 6px;
  background: #FEF2F2; border: 1px solid #FECACA;
  border-radius: 8px; padding: 8px 12px;
  color: #DC2626; font-size: 0.8rem; font-weight: 500;
  margin-bottom: 16px;
}

/* ── Sign In button ───────────────────────────────── */
.login-btn-signin {
  width: 100%; padding: 13px;
  background: linear-gradient(135deg, #4F6BFF 0%, #7C3AFF 100%);
  color: #ffffff; border: none; border-radius: 50px;
  font-size: 0.92rem; font-weight: 700; letter-spacing: 0.04em;
  cursor: pointer; margin-bottom: 12px;
  transition: opacity 0.15s, transform 0.1s, box-shadow 0.15s;
  box-shadow: 0 4px 14px rgba(79, 107, 255, 0.35);
  font-family: inherit;

  &:hover { opacity: 0.92; box-shadow: 0 6px 18px rgba(79, 107, 255, 0.45); }
  &:active { transform: scale(0.98); }
}

/* ── Forgot Password button ───────────────────────── */
.login-btn-forgot {
  width: 100%; padding: 11px;
  background: transparent;
  color: #4F6BFF; border: 1.5px solid #C7D2FE;
  border-radius: 50px; font-size: 0.88rem; font-weight: 600;
  cursor: pointer; margin-bottom: 20px;
  transition: background 0.15s, border-color 0.15s;
  font-family: inherit;

  &:hover { background: #EEF2FF; border-color: #4F6BFF; }
}

/* ── Language switch ──────────────────────────────── */
.login-lang {
  display: flex; justify-content: center;
}

/* ── Success message ──────────────────────────────── */
.login-success {
  display: flex; align-items: center; gap: 6px;
  background: #F0FDF4; border: 1px solid #BBF7D0;
  border-radius: 8px; padding: 8px 12px;
  color: #16A34A; font-size: 0.8rem; font-weight: 500;
  margin-bottom: 16px;
}

/* ── OTP step ─────────────────────────────────────── */
.otp-header { margin-bottom: 24px; text-align: center; }
.otp-title { font-size: 1.1rem; font-weight: 700; color: #1E293B; margin: 0 0 6px; }
.otp-subtitle { font-size: 0.82rem; color: #64748B; margin: 0; line-height: 1.5; }
.otp-input { letter-spacing: 0.3em; font-size: 1.2rem; font-weight: 700; text-align: center; }
.login-btn-back {
  width: 100%; padding: 10px;
  background: transparent; color: #94A3B8;
  border: none; font-size: 0.83rem; font-weight: 500;
  cursor: pointer; font-family: inherit;
  &:hover { color: #4F6BFF; }
}
</style>